/**
 * LabelTemplateManager
 *
 * CRUD interface for managing label templates.
 * Embedded in Settings page. Allows create, edit, duplicate, delete, preview.
 * Uses a visual drag-and-drop canvas editor with a side properties panel.
 */
import { Add, ContentCopy, Delete, Edit } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useLabelTemplates } from '../../hooks/useLabelTemplates';
import { generatePreviewElements } from '../../services/printing';
import type { LabelTemplate, LabelTemplateElement } from '../../types/printing';
import { ElementPropertiesPanel } from './ElementPropertiesPanel';
import { LabelPreviewRenderer } from './LabelPreviewRenderer';
import { VisualLabelCanvas } from './VisualLabelCanvas';

// Mock asset for template preview
const PREVIEW_ASSET = {
  id: 'preview-id',
  customer_id: 'preview-cust',
  tag_id: 'AST-PREVIEW-001',
  serial_number: 'SN-12345-ABCDE',
  type: 'HVAC Unit',
  status: 'active' as const,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  customer: {
    id: 'preview-cust',
    name: 'Acme Corporation',
    email: null,
    phone: null,
    address: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
};

type ElementType = LabelTemplateElement['type'];

const ELEMENT_TYPE_OPTIONS: { value: ElementType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'fieldblock', label: 'Text Block' },
  { value: 'qrcode', label: 'QR Code' },
  { value: 'barcode', label: 'Barcode' },
  { value: 'box', label: 'Box / Border' },
  { value: 'line', label: 'Line' },
  { value: 'inverse-box', label: 'Inverse Box' },
];

interface TemplateFormData {
  name: string;
  description: string;
  width_dots: number;
  height_dots: number;
  rotation: 0 | 90 | 180 | 270;
  elements: LabelTemplateElement[];
  is_default: boolean;
}

const EMPTY_FORM: TemplateFormData = {
  name: '',
  description: '',
  width_dots: 406,
  height_dots: 406,
  rotation: 0,
  elements: [],
  is_default: false,
};

function templateToForm(t: LabelTemplate): TemplateFormData {
  return {
    name: t.name,
    description: t.description || '',
    width_dots: t.width_dots,
    height_dots: t.height_dots,
    rotation: t.rotation as 0 | 90 | 180 | 270,
    elements: (t.elements as LabelTemplateElement[]) || [],
    is_default: t.is_default,
  };
}

function createBlankElement(type: ElementType): LabelTemplateElement {
  const baseOpts = { x: '10%', y: '10%' };
  switch (type) {
    case 'text':
      return { type: 'text', content: '{AssetType}', options: { ...baseOpts, fontSize: 30 } };
    case 'fieldblock':
      return {
        type: 'fieldblock',
        content: '{SerialNumber}',
        options: { ...baseOpts, width: 200, fontSize: 24, max: 2 },
      };
    case 'qrcode':
      return {
        type: 'qrcode',
        content: '{QRUrl}',
        options: { ...baseOpts, magnificationFactor: 5, errorCorrection: 'M' as const },
      };
    case 'barcode':
      return { type: 'barcode', content: '{TagId}', options: { ...baseOpts, height: 60 } };
    case 'box':
      return { type: 'box', options: { ...baseOpts, width: 100, height: 100, borderThickness: 2 } };
    case 'line':
      return { type: 'line', options: { ...baseOpts, width: 200, height: 2, thickness: 2 } };
    case 'inverse-box':
      return {
        type: 'inverse-box',
        options: { ...baseOpts, width: 200, height: 40, borderThickness: 40 },
      };
    default:
      return { type: 'text', content: 'Text', options: { ...baseOpts, fontSize: 24 } };
  }
}

export function LabelTemplateManager() {
  const {
    templates,
    loading,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
  } = useLabelTemplates();
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TemplateFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [selectedElementIndex, setSelectedElementIndex] = useState<number | null>(null);

  const handleCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSaveError(null);
    setSelectedElementIndex(null);
    setEditOpen(true);
  };

  const handleEdit = (template: LabelTemplate) => {
    setEditingId(template.id);
    setForm(templateToForm(template));
    setSaveError(null);
    setSelectedElementIndex(null);
    setEditOpen(true);
  };

  const handleDuplicate = async (template: LabelTemplate) => {
    await duplicateTemplate(template.id, `${template.name} (Copy)`);
  };

  const handleDelete = async (id: string) => {
    await deleteTemplate(id);
    setDeleteConfirmId(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        name: form.name,
        description: form.description || undefined,
        width_dots: form.width_dots,
        height_dots: form.height_dots,
        rotation: form.rotation,
        elements: form.elements,
        is_default: form.is_default,
      };

      if (editingId) {
        await updateTemplate(editingId, payload);
      } else {
        await createTemplate(payload);
      }
      setEditOpen(false);
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      // Improve message for common RLS / permission errors
      const isRLS =
        raw.includes('row-level security') ||
        raw.includes('policy') ||
        raw.includes('new row violates');
      const message = isRLS
        ? 'Permission denied. Your account role does not allow saving templates. Ask an admin to assign you the technician, manager, or admin role.'
        : `Failed to save template: ${raw}`;
      setSaveError(message);
      console.error('[LabelTemplateManager] Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateFormField = useCallback(
    <K extends keyof TemplateFormData>(field: K, value: TemplateFormData[K]) => {
      setForm(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  const updateElement = useCallback((index: number, field: string, value: unknown) => {
    setForm(prev => ({
      ...prev,
      elements: prev.elements.map((el, i) => {
        if (i !== index) return el;
        if (field === 'content') {
          return { ...el, content: value as string };
        }
        // Everything else goes into options
        return {
          ...el,
          options: { ...((el.options || {}) as Record<string, unknown>), [field]: value },
        };
      }),
    }));
  }, []);

  const removeElement = useCallback((index: number) => {
    setForm(prev => ({
      ...prev,
      elements: prev.elements.filter((_, i) => i !== index),
    }));
  }, []);

  const addElement = useCallback(
    (type: ElementType) => {
      setForm(prev => {
        const newElements = [...prev.elements, createBlankElement(type)];
        return { ...prev, elements: newElements };
      });
      // Select the newly added element
      setSelectedElementIndex(form.elements.length);
    },
    [form.elements.length]
  );

  const moveElement = useCallback((index: number, newX: string | number, newY: string | number) => {
    setForm(prev => ({
      ...prev,
      elements: prev.elements.map((el, i) => {
        if (i !== index) return el;
        return {
          ...el,
          options: { ...((el.options || {}) as Record<string, unknown>), x: newX, y: newY },
        };
      }),
    }));
  }, []);

  const reorderElement = useCallback((index: number, direction: 'up' | 'down') => {
    setForm(prev => {
      const newElements = [...prev.elements];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= newElements.length) return prev;
      [newElements[index], newElements[targetIndex]] = [
        newElements[targetIndex],
        newElements[index],
      ];
      return { ...prev, elements: newElements };
    });
    setSelectedElementIndex(prev =>
      prev === index ? (direction === 'up' ? index - 1 : index + 1) : prev
    );
  }, []);

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Label Templates</Typography>
        <Button startIcon={<Add />} variant="contained" size="small" onClick={handleCreate}>
          New Template
        </Button>
      </Stack>

      {loading ? (
        <Typography color="text.secondary">Loading templates…</Typography>
      ) : templates.length === 0 ? (
        <Typography color="text.secondary">
          No templates found. Create one to get started.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {templates.map(t => {
            const elems = generatePreviewElements(PREVIEW_ASSET, t, { companyName: 'DigniCRM' });
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={t.id}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', pb: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">{t.name}</Typography>
                      {t.is_default && <Chip label="Default" size="small" color="primary" />}
                    </Stack>
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                      <LabelPreviewRenderer
                        elements={elems}
                        widthDots={t.width_dots}
                        heightDots={t.height_dots}
                        scale={0.35}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {(t.width_dots / 203).toFixed(1)}&quot; × {(t.height_dots / 203).toFixed(1)}
                      &quot; • {(t.elements as LabelTemplateElement[]).length} elements
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(t)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                      <IconButton size="small" onClick={() => handleDuplicate(t)}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteConfirmId(t.id)}
                        disabled={t.is_default}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Template?</DialogTitle>
        <DialogContent>
          <Typography>
            This cannot be undone. Print jobs using this template will retain their generated ZPL.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create / Edit Dialog — visual editor */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ py: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{editingId ? 'Edit Template' : 'Create Template'}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Add element dropdown - always visible in toolbar */}
              <Select
                value=""
                size="small"
                displayEmpty
                onChange={e => {
                  if (e.target.value) addElement(e.target.value as ElementType);
                }}
                sx={{ minWidth: 160 }}
                renderValue={v => (v ? String(v) : '+ Add Element')}
              >
                {ELEMENT_TYPE_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          {saveError && (
            <Alert
              severity="error"
              sx={{ mx: 2, mt: 1, flexShrink: 0 }}
              onClose={() => setSaveError(null)}
            >
              {saveError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            {/* Left sidebar — template settings */}
            <Box
              sx={{
                width: 260,
                minWidth: 260,
                borderRight: 1,
                borderColor: 'divider',
                overflowY: 'auto',
                p: 2,
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1, display: 'block' }}
              >
                Template Settings
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  label="Name"
                  value={form.name}
                  onChange={e => updateFormField('name', e.target.value)}
                  fullWidth
                  required
                  size="small"
                />
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={e => updateFormField('description', e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
                <Stack direction="row" spacing={1}>
                  <TextField
                    label="Width"
                    type="number"
                    value={form.width_dots}
                    onChange={e => updateFormField('width_dots', parseInt(e.target.value) || 203)}
                    size="small"
                    sx={{ flex: 1 }}
                    helperText={`${(form.width_dots / 203).toFixed(1)}"`}
                  />
                  <TextField
                    label="Height"
                    type="number"
                    value={form.height_dots}
                    onChange={e => updateFormField('height_dots', parseInt(e.target.value) || 203)}
                    size="small"
                    sx={{ flex: 1 }}
                    helperText={`${(form.height_dots / 203).toFixed(1)}"`}
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Select
                    value={form.rotation}
                    onChange={e =>
                      updateFormField('rotation', e.target.value as 0 | 90 | 180 | 270)
                    }
                    size="small"
                    sx={{ flex: 1 }}
                  >
                    <MenuItem value={0}>0°</MenuItem>
                    <MenuItem value={90}>90°</MenuItem>
                    <MenuItem value={180}>180°</MenuItem>
                    <MenuItem value={270}>270°</MenuItem>
                  </Select>
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_default}
                      onChange={e => updateFormField('is_default', e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Default template</Typography>}
                />
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Element list */}
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1, display: 'block' }}
              >
                Elements ({form.elements.length})
              </Typography>
              <Stack spacing={0.5}>
                {form.elements.map((el, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    onClick={() => setSelectedElementIndex(idx)}
                    sx={{
                      p: 0.75,
                      cursor: 'pointer',
                      bgcolor: selectedElementIndex === idx ? 'primary.50' : 'transparent',
                      borderColor: selectedElementIndex === idx ? 'primary.main' : 'divider',
                      '&:hover': {
                        bgcolor: selectedElementIndex === idx ? 'primary.50' : 'action.hover',
                      },
                    }}
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip
                        label={el.type}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                      <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                        {'content' in el ? String(el.content) : ''}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
                {form.elements.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ py: 1 }}>
                    Add elements using the toolbar above.
                  </Typography>
                )}
              </Stack>
            </Box>

            {/* Center — visual canvas */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.100',
                overflow: 'auto',
                p: 3,
              }}
            >
              <Box>
                <VisualLabelCanvas
                  elements={form.elements}
                  widthDots={form.width_dots}
                  heightDots={form.height_dots}
                  scale={0.7}
                  selectedIndex={selectedElementIndex}
                  onSelectElement={setSelectedElementIndex}
                  onMoveElement={moveElement}
                  templateId={editingId || undefined}
                  templateMeta={{
                    name: form.name || 'Preview',
                    description: form.description || null,
                    rotation: form.rotation,
                    is_default: form.is_default,
                    created_by: null,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                >
                  Click to select · Drag to reposition · Variables resolved at print time
                </Typography>
              </Box>
            </Box>

            {/* Right sidebar — element properties */}
            <Box
              sx={{
                width: 280,
                minWidth: 280,
                borderLeft: 1,
                borderColor: 'divider',
                overflowY: 'auto',
                p: 2,
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1, display: 'block' }}
              >
                Element Properties
              </Typography>
              {selectedElementIndex !== null && form.elements[selectedElementIndex] ? (
                <ElementPropertiesPanel
                  element={form.elements[selectedElementIndex]}
                  index={selectedElementIndex}
                  onUpdate={updateElement}
                  onRemove={idx => {
                    removeElement(idx);
                    setSelectedElementIndex(null);
                  }}
                  onMoveUp={idx => reorderElement(idx, 'up')}
                  onMoveDown={idx => reorderElement(idx, 'down')}
                  isFirst={selectedElementIndex === 0}
                  isLast={selectedElementIndex === form.elements.length - 1}
                />
              ) : (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Select an element on the canvas or in the list to edit its properties.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name}>
            {saving ? 'Saving…' : editingId ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
